import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  X,
  Play,
  Pause,
  Minimize2,
  Maximize2,
  GripVertical,
  Eye,
  Type,
  ChevronUp,
  ChevronDown,
  RotateCcw,
  FlipHorizontal,
  Focus,
  Zap,
  Loader2
} from "lucide-react";
import requests from "../../services/httpServices";

// Font size mapping from settings ('xs', 's', 'm', 'l', 'xl') to pixels
const FONT_SIZE_MAP = {
  xs: 20,
  s: 24,
  m: 28,
  l: 36,
  xl: 48,
};

// Line height mapping
const LINE_HEIGHT_MAP = {
  tight: 1.4,
  normal: 1.6,
  relaxed: 2.0,
};

const FloatingTeleprompter = ({
  text = "",
  content = "",
  script = "",
  font = "Inter, system-ui, sans-serif",
  fontSize = "m", // Can be 'xs', 's', 'm', 'l', 'xl' or a number
  lineHeight = "normal", // Can be 'tight', 'normal', 'relaxed'
  scrollSpeed = 10,
  speed = 10, // Alias for scrollSpeed
  mirroring = false,
  highlightActiveLine = true,
  focusMode = false,
  focusLines = 3,
  countdownSeconds = 0,
  theme = "dark",
  textAlign = "center",
  autoDetectSpeed: autoDetectSpeedProp = false,
  onClose,
  initialPosition = { x: 100, y: 100 },
  initialSize = { width: 500, height: 350 },
}) => {
  // Get the actual text content from various possible props
  const displayText = text || content || script || "";

  // Parse font size - can be string key or number
  const getInitialFontSize = () => {
    if (typeof fontSize === 'number') return fontSize;
    return FONT_SIZE_MAP[fontSize] || FONT_SIZE_MAP.m;
  };

  // Parse line height
  const getLineHeight = () => {
    if (typeof lineHeight === 'number') return lineHeight;
    return LINE_HEIGHT_MAP[lineHeight] || LINE_HEIGHT_MAP.normal;
  };

  // Use scrollSpeed or speed prop
  const actualSpeed = scrollSpeed || speed || 10;

  // Position and size state
  const [position, setPosition] = useState(initialPosition);
  const [size, setSize] = useState(initialSize);
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [resizeDirection, setResizeDirection] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Teleprompter state
  const [isPlaying, setIsPlaying] = useState(false);
  const [scrollPos, setScrollPos] = useState(0); // Track scroll position for slider
  const [opacity, setOpacity] = useState(0.55); // More transparent by default
  const [fontWeight, setFontWeight] = useState(600);
  const [currentFontSize, setCurrentFontSize] = useState(getInitialFontSize());
  const [currentLineHeight, setCurrentLineHeight] = useState(getLineHeight());
  const [isMirrored, setIsMirrored] = useState(mirroring);
  const [isFocusMode, setIsFocusMode] = useState(focusMode);
  const [showControls, setShowControls] = useState(true);
  const [isMinimized, setIsMinimized] = useState(false);
  const [countdown, setCountdown] = useState(0);

  // Refs
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const scrollRef = useRef(null);
  const scrollIntervalRef = useRef(null);
  const hasStartedRef = useRef(false);
  const countdownIntervalRef = useRef(null);

  // AI Auto-detect state
  const [aiAutoDetect, setAiAutoDetect] = useState(autoDetectSpeedProp);
  const [aiListening, setAiListening] = useState(false);
  const [aiProcessing, setAiProcessing] = useState(false);
  const [matchedWordIndex, setMatchedWordIndex] = useState(-1); // index of last matched word in scriptWords
  const [lastHeard, setLastHeard] = useState(""); // show what mic picks up for debugging
  // Refs to avoid stale closures in recognition callbacks
  const aiAutoDetectRef = useRef(autoDetectSpeedProp);
  const isPlayingRef = useRef(false);

  // Keep refs in sync
  useEffect(() => { aiAutoDetectRef.current = aiAutoDetect; }, [aiAutoDetect]);
  useEffect(() => { isPlayingRef.current = isPlaying; }, [isPlaying]);
  const recognitionRef = useRef(null);
  const micStreamRef = useRef(null); // separate mic stream for speech recognition
  const speechBufferRef = useRef(""); // accumulated finalized speech
  const interimBufferRef = useRef(""); // current interim (not yet finalized)
  const aiSentenceIndexRef = useRef(0);
  const aiDetectTimerRef = useRef(null);
  const matchedWordIndexRef = useRef(-1);

  // Smooth scroll animation state
  const aiScrollAnimRef = useRef(null); // requestAnimationFrame ID
  const aiScrollCurrentRef = useRef(0); // current animated scroll position
  const aiScrollTargetRef = useRef(0); // target scroll position
  const aiScrollVelocityRef = useRef(0);
  const lastMatchTimeRef = useRef(Date.now());

  // Normalize a word for fuzzy matching: lowercase, strip punctuation
  const normalizeWord = useCallback((w) => {
    return w.toLowerCase().replace(/[^a-z0-9'']/g, '');
  }, []);

  // Split display text into individual words for matching and rendering
  const scriptWordsRef = useRef([]);
  const wordToIndexMapRef = useRef(new Map()); // normalized word -> array of indices
  useEffect(() => {
    const fullText = text || content || script || "";
    // Split into words preserving whitespace info for rendering
    const words = fullText.split(/(\s+)/).filter(Boolean);
    const scriptWords = [];
    const wordMap = new Map();

    words.forEach((token) => {
      if (/^\s+$/.test(token)) {
        // whitespace token — mark it
        scriptWords.push({ raw: token, normalized: '', isSpace: true });
      } else {
        const norm = token.toLowerCase().replace(/[^a-z0-9'']/g, '');
        const idx = scriptWords.length;
        scriptWords.push({ raw: token, normalized: norm, isSpace: false, index: idx });
        if (norm) {
          if (!wordMap.has(norm)) wordMap.set(norm, []);
          wordMap.get(norm).push(idx);
        }
      }
    });
    scriptWordsRef.current = scriptWords;
    wordToIndexMapRef.current = wordMap;
  }, [text, content, script]);

  // Split display text into sentences for AI tracking
  const sentencesRef = useRef([]);
  useEffect(() => {
    const fullText = text || content || script || "";
    sentencesRef.current = fullText
      .replace(/\n+/g, ' ')
      .split(/(?<=[.!?])\s+/)
      .map(s => s.trim())
      .filter(Boolean);
  }, [text, content, script]);

  // Client-side fuzzy word matching: advance matchedWordIndex as speech words come in
  const doClientSideMatch = useCallback((spokenText) => {
    if (!spokenText) return;
    const spokenWords = spokenText.toLowerCase().split(/\s+/).filter(Boolean);
    if (spokenWords.length === 0) return;

    const scriptWords = scriptWordsRef.current;
    const actualWords = scriptWords.filter(w => !w.isSpace);
    let currentIdx = matchedWordIndexRef.current;

    // Look at last few spoken words and try to match them going forward in script
    const recentSpoken = spokenWords.slice(-8); // last 8 spoken words
    const searchStart = Math.max(0, currentIdx);
    // Look ahead up to ~100 actual words from current position (2 sentences worth)
    const searchEnd = Math.min(actualWords.length, searchStart + 100);

    for (let si = 0; si < recentSpoken.length; si++) {
      const spokenNorm = recentSpoken[si].replace(/[^a-z0-9'']/g, '');
      if (!spokenNorm || spokenNorm.length < 2) continue;

      // Search forward from current position
      for (let wi = searchStart; wi < searchEnd; wi++) {
        const scriptNorm = actualWords[wi]?.normalized;
        if (!scriptNorm) continue;

        // Exact match or starts-with (for partial words)
        if (scriptNorm === spokenNorm || scriptNorm.startsWith(spokenNorm) || spokenNorm.startsWith(scriptNorm)) {
          // Only advance, never go backwards
          const scriptIdx = actualWords[wi].index;
          if (scriptIdx > currentIdx) {
            currentIdx = scriptIdx;
            lastMatchTimeRef.current = Date.now();
            break;
          }
        }
      }
    }

    if (currentIdx !== matchedWordIndexRef.current) {
      matchedWordIndexRef.current = currentIdx;
      setMatchedWordIndex(currentIdx);

      // Calculate scroll target based on matched word position
      if (scrollRef.current && contentRef.current) {
        // Find the word element in the DOM
        const wordEl = contentRef.current.querySelector(`[data-word-idx="${currentIdx}"]`);
        if (wordEl) {
          const containerRect = scrollRef.current.getBoundingClientRect();
          const wordRect = wordEl.getBoundingClientRect();
          const relativeTop = wordRect.top - containerRect.top + scrollRef.current.scrollTop;
          // Scroll so the matched word is about 1/3 from the top
          const targetScroll = relativeTop - (containerRect.height * 0.33);
          aiScrollTargetRef.current = Math.max(0, targetScroll);
        }
      }
    }
  }, []);

  // AI Auto-detect: Start/stop Web Speech API listening
  useEffect(() => {
    if (!aiAutoDetect || !isPlaying) {
      // Stop listening
      if (recognitionRef.current) {
        recognitionRef.current.abort();
        recognitionRef.current = null;
      }
      if (aiDetectTimerRef.current) {
        clearInterval(aiDetectTimerRef.current);
        aiDetectTimerRef.current = null;
      }
      if (aiScrollAnimRef.current) {
        cancelAnimationFrame(aiScrollAnimRef.current);
        aiScrollAnimRef.current = null;
      }
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach(t => t.stop());
        micStreamRef.current = null;
      }
      setAiListening(false);
      speechBufferRef.current = "";
      interimBufferRef.current = "";
      return;
    }

    // Start Web Speech API
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      console.warn('[Teleprompter AI] Web Speech API not supported');
      return;
    }

    // Request a fresh mic stream — ensures Chrome's Web Speech API can
    // access the microphone even when Agora already has a track open
    let micStream = null;
    const startRecognition = async () => {
      try {
        micStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        micStreamRef.current = micStream;
        console.log('[Teleprompter AI] Got mic stream for speech recognition');
      } catch (err) {
        console.warn('[Teleprompter AI] Could not get mic stream, trying without:', err);
      }

      const recognition = new SpeechRecognition();
      recognition.continuous = true;
      recognition.interimResults = true;
      recognition.lang = 'en-US';
      recognition.maxAlternatives = 1;

      recognition.onresult = (event) => {
        let finalTranscript = '';
        let interimTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; i++) {
          const result = event.results[i];
          if (result.isFinal) {
            finalTranscript += result[0].transcript + ' ';
          } else {
            interimTranscript += result[0].transcript;
          }
        }

        // Accumulate finalized speech (never overwrite)
        if (finalTranscript) {
          speechBufferRef.current += finalTranscript;
          setLastHeard(finalTranscript.trim().split(/\s+/).slice(-6).join(' '));
          doClientSideMatch(speechBufferRef.current);
        }

        // Store interim for real-time matching too
        interimBufferRef.current = interimTranscript;
        if (interimTranscript) {
          setLastHeard(interimTranscript.trim().split(/\s+/).slice(-6).join(' '));
          doClientSideMatch(speechBufferRef.current + ' ' + interimTranscript);
        }
      };

      recognition.onstart = () => setAiListening(true);

      recognition.onend = () => {
        if (aiAutoDetectRef.current && isPlayingRef.current) {
          setTimeout(() => {
            try { recognition.start(); } catch (e) { /* already started */ }
          }, 200);
        } else {
          setAiListening(false);
          if (micStream) {
            micStream.getTracks().forEach(t => t.stop());
            micStream = null;
          }
        }
      };

      recognition.onerror = (e) => {
        console.warn('[Teleprompter AI] Speech error:', e.error);
      };

      try {
        recognition.start();
        recognitionRef.current = recognition;
      } catch (e) {
        console.warn('[Teleprompter AI] Could not start speech recognition:', e);
      }
    };

    startRecognition();

    // Periodically send accumulated speech to GPT for refinement (backup to client-side matching)
    aiDetectTimerRef.current = setInterval(async () => {
      const recentSpeech = speechBufferRef.current.trim();
      if (!recentSpeech || recentSpeech.length < 15) return;

      const fullScript = (text || content || script || "");
      if (!fullScript) return;

      setAiProcessing(true);
      try {
        const resp = await requests.post('/openai/prompter-detect-position', {
          script: fullScript,
          currentSentenceIndex: aiSentenceIndexRef.current,
          recentSpeech: recentSpeech.slice(-300), // last 300 chars
          lookAheadSentences: 2,
        });

        const { sentenceIndex, confidence } = resp;
        if (confidence >= 0.4 && sentenceIndex !== aiSentenceIndexRef.current) {
          aiSentenceIndexRef.current = sentenceIndex;

          // Map sentence index to a word index for more precise scroll targeting
          const sentences = sentencesRef.current;
          if (sentences.length > 0 && sentenceIndex < sentences.length) {
            // Find the first word of the target sentence in the script
            const targetSentence = sentences[sentenceIndex];
            const firstWords = targetSentence.split(/\s+/).slice(0, 3).map(w => w.toLowerCase().replace(/[^a-z0-9]/g, ''));
            const scriptWords = scriptWordsRef.current.filter(w => !w.isSpace);

            for (let i = Math.max(0, matchedWordIndexRef.current); i < scriptWords.length; i++) {
              if (firstWords[0] && scriptWords[i].normalized === firstWords[0]) {
                // Verify next word too if possible
                if (!firstWords[1] || (scriptWords[i + 1] && scriptWords[i + 1].normalized === firstWords[1])) {
                  const newIdx = scriptWords[i].index;
                  if (newIdx > matchedWordIndexRef.current) {
                    matchedWordIndexRef.current = newIdx;
                    setMatchedWordIndex(newIdx);
                    lastMatchTimeRef.current = Date.now();

                    // Update scroll target
                    if (scrollRef.current && contentRef.current) {
                      const wordEl = contentRef.current.querySelector(`[data-word-idx="${newIdx}"]`);
                      if (wordEl) {
                        const containerRect = scrollRef.current.getBoundingClientRect();
                        const wordRect = wordEl.getBoundingClientRect();
                        const relativeTop = wordRect.top - containerRect.top + scrollRef.current.scrollTop;
                        aiScrollTargetRef.current = Math.max(0, relativeTop - (containerRect.height * 0.33));
                      }
                    }
                  }
                  break;
                }
              }
            }
          }
        }
      } catch (err) {
        // Silent fail - client-side matching continues working
      } finally {
        setAiProcessing(false);
      }

      // Trim buffer to last 500 chars to avoid growing forever
      if (speechBufferRef.current.length > 500) {
        speechBufferRef.current = speechBufferRef.current.slice(-500);
      }
    }, 4000); // Check every 4 seconds

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.abort();
        recognitionRef.current = null;
      }
      if (aiDetectTimerRef.current) {
        clearInterval(aiDetectTimerRef.current);
        aiDetectTimerRef.current = null;
      }
      if (micStreamRef.current) {
        micStreamRef.current.getTracks().forEach(t => t.stop());
        micStreamRef.current = null;
      }
    };
  }, [aiAutoDetect, isPlaying, text, content, script, doClientSideMatch]);

  // AI auto-detect: smooth easing scroll animation using requestAnimationFrame
  useEffect(() => {
    if (!aiAutoDetect || !isPlaying) return;

    const EASING_SPEED = 0.06; // How fast to ease toward target (0-1, lower = smoother)
    const STOP_THRESHOLD = 0.5; // Stop animating when within this many pixels
    const OFF_SCRIPT_TIMEOUT = 5000; // ms without a match before easing to stop

    const animate = () => {
      if (!scrollRef.current) {
        aiScrollAnimRef.current = requestAnimationFrame(animate);
        return;
      }

      const current = scrollRef.current.scrollTop;
      const target = aiScrollTargetRef.current;
      const timeSinceMatch = Date.now() - lastMatchTimeRef.current;

      // If speaker hasn't matched in a while, don't scroll (off-script)
      if (timeSinceMatch > OFF_SCRIPT_TIMEOUT) {
        // Ease to a stop by not updating target
        aiScrollAnimRef.current = requestAnimationFrame(animate);
        return;
      }

      const diff = target - current;
      if (Math.abs(diff) > STOP_THRESHOLD) {
        // Ease toward target
        const step = diff * EASING_SPEED;
        scrollRef.current.scrollTop = current + step;
      }

      aiScrollAnimRef.current = requestAnimationFrame(animate);
    };

    // Initialize scroll position
    if (scrollRef.current) {
      aiScrollCurrentRef.current = scrollRef.current.scrollTop;
      aiScrollTargetRef.current = scrollRef.current.scrollTop;
    }

    aiScrollAnimRef.current = requestAnimationFrame(animate);

    return () => {
      if (aiScrollAnimRef.current) {
        cancelAnimationFrame(aiScrollAnimRef.current);
        aiScrollAnimRef.current = null;
      }
    };
  }, [aiAutoDetect, isPlaying]);

  // Minimum dimensions
  const MIN_WIDTH = 300;
  const MIN_HEIGHT = 200;
  const MAX_WIDTH = window.innerWidth - 100;
  const MAX_HEIGHT = window.innerHeight - 100;

  // Handle dragging
  const handleMouseDown = useCallback((e) => {
    if (e.target.closest('.control-area') || e.target.closest('.resize-handle')) return;

    setIsDragging(true);
    const rect = containerRef.current.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      const newX = Math.max(0, Math.min(window.innerWidth - size.width, e.clientX - dragOffset.x));
      const newY = Math.max(0, Math.min(window.innerHeight - size.height, e.clientY - dragOffset.y));
      setPosition({ x: newX, y: newY });
    }

    if (isResizing && resizeDirection) {
      const rect = containerRef.current.getBoundingClientRect();

      if (resizeDirection.includes('e')) {
        const newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, e.clientX - rect.left));
        setSize(prev => ({ ...prev, width: newWidth }));
      }
      if (resizeDirection.includes('s')) {
        const newHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, e.clientY - rect.top));
        setSize(prev => ({ ...prev, height: newHeight }));
      }
      if (resizeDirection.includes('w')) {
        const diff = rect.left - e.clientX;
        const newWidth = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, rect.width + diff));
        if (newWidth !== rect.width) {
          setSize(prev => ({ ...prev, width: newWidth }));
          setPosition(prev => ({ ...prev, x: e.clientX }));
        }
      }
      if (resizeDirection.includes('n')) {
        const diff = rect.top - e.clientY;
        const newHeight = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, rect.height + diff));
        if (newHeight !== rect.height) {
          setSize(prev => ({ ...prev, height: newHeight }));
          setPosition(prev => ({ ...prev, y: e.clientY }));
        }
      }
    }
  }, [isDragging, isResizing, resizeDirection, dragOffset, size.width, size.height]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    setResizeDirection(null);
  }, []);

  // Handle resize start
  const handleResizeStart = useCallback((direction) => (e) => {
    e.stopPropagation();
    setIsResizing(true);
    setResizeDirection(direction);
  }, []);

  // Mouse event listeners
  useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, isResizing, handleMouseMove, handleMouseUp]);

  // Handle play/pause with optional countdown
  const handlePlayPause = useCallback(() => {
    if (isPlaying) {
      // Pause
      if (scrollIntervalRef.current) {
        clearInterval(scrollIntervalRef.current);
        scrollIntervalRef.current = null;
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      setCountdown(0);
      setIsPlaying(false);
    } else {
      // Play with optional countdown
      if (countdownSeconds > 0 && !hasStartedRef.current) {
        setCountdown(countdownSeconds);
        countdownIntervalRef.current = setInterval(() => {
          setCountdown(prev => {
            if (prev <= 1) {
              clearInterval(countdownIntervalRef.current);
              countdownIntervalRef.current = null;
              startScrolling();
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        startScrolling();
      }
    }
  }, [isPlaying, countdownSeconds]);

  const startScrolling = useCallback(() => {
    if (!hasStartedRef.current && scrollRef.current && contentRef.current) {
      hasStartedRef.current = true;
      const containerHeight = scrollRef.current.clientHeight;
      contentRef.current.style.paddingTop = `${containerHeight}px`;
      contentRef.current.style.paddingBottom = `${containerHeight}px`;
      scrollRef.current.scrollTop = 0;
    }
    // Auto-enable AI listening when Play is pressed (no separate button needed)
    setAiAutoDetect(true);
    setIsPlaying(true);
  }, []);

  // Scrolling effect (disabled when AI auto-detect is active)
  useEffect(() => {
    if (isPlaying && scrollRef.current && actualSpeed > 0 && !aiAutoDetect) {
      const pixelsPerSecond = (actualSpeed / 100) * 150;
      const fps = 60;
      const scrollAmount = pixelsPerSecond / fps;
      const intervalTime = 1000 / fps;

      scrollIntervalRef.current = setInterval(() => {
        if (scrollRef.current) {
          const currentScroll = scrollRef.current.scrollTop;
          const maxScroll = scrollRef.current.scrollHeight - scrollRef.current.clientHeight;

          if (currentScroll >= maxScroll) {
            scrollRef.current.scrollTop = 0;
          } else {
            scrollRef.current.scrollTop = currentScroll + scrollAmount;
          }
        }
      }, intervalTime);

      return () => {
        if (scrollIntervalRef.current) {
          clearInterval(scrollIntervalRef.current);
        }
      };
    }
  }, [isPlaying, actualSpeed, aiAutoDetect]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't capture if typing in an input
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

      if (e.code === 'Space' && actualSpeed > 0) {
        e.preventDefault();
        handlePlayPause();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        if (scrollRef.current) {
          scrollRef.current.scrollTop -= 50;
        }
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        if (scrollRef.current) {
          scrollRef.current.scrollTop += 50;
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [actualSpeed, handlePlayPause]);

  // Reset scroll
  const handleReset = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
    if (contentRef.current) {
      contentRef.current.style.paddingTop = '2rem';
      contentRef.current.style.paddingBottom = '2rem';
    }
    hasStartedRef.current = false;
    setIsPlaying(false);
    setCountdown(0);
    if (scrollIntervalRef.current) {
      clearInterval(scrollIntervalRef.current);
      scrollIntervalRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    // Reset AI state
    matchedWordIndexRef.current = -1;
    setMatchedWordIndex(-1);
    aiSentenceIndexRef.current = 0;
    speechBufferRef.current = "";
    interimBufferRef.current = "";
    aiScrollTargetRef.current = 0;
    lastMatchTimeRef.current = Date.now();
  };

  // Toggle minimize
  const handleMinimize = () => {
    setIsMinimized(!isMinimized);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (scrollIntervalRef.current) clearInterval(scrollIntervalRef.current);
      if (countdownIntervalRef.current) clearInterval(countdownIntervalRef.current);
      if (recognitionRef.current) { recognitionRef.current.abort(); recognitionRef.current = null; }
      if (aiDetectTimerRef.current) { clearInterval(aiDetectTimerRef.current); aiDetectTimerRef.current = null; }
      if (aiScrollAnimRef.current) { cancelAnimationFrame(aiScrollAnimRef.current); aiScrollAnimRef.current = null; }
    };
  }, []);

  if (isMinimized) {
    return (
      <div
        ref={containerRef}
        className={`fixed z-[100] cursor-grab transition-all duration-200 ${isDragging ? 'cursor-grabbing ring-2 ring-purple-500/50' : 'hover:ring-1 hover:ring-white/30'}`}
        style={{
          left: position.x,
          top: position.y,
          backgroundColor: `rgba(0, 0, 0, ${opacity})`,
          backdropFilter: 'blur(8px)',
          borderRadius: '12px',
          boxShadow: isDragging ? '0 12px 48px rgba(106, 31, 191, 0.3)' : '0 8px 32px rgba(0, 0, 0, 0.4)',
          border: `1px solid rgba(255, 255, 255, ${isDragging ? '0.3' : '0.1'})`,
        }}
        onMouseDown={handleMouseDown}
      >
        <div className="flex items-center gap-2 px-4 py-2 text-white">
          <GripVertical className="w-4 h-4 text-gray-400 animate-pulse" />
          <span className="text-sm font-medium">Teleprompter</span>
          {isPlaying && <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />}
          <button
            onClick={(e) => { e.stopPropagation(); handlePlayPause(); }}
            className="p-1 hover:bg-white/20 rounded transition-colors ml-2"
          >
            {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); handleMinimize(); }}
            className="p-1 hover:bg-white/20 rounded transition-colors"
          >
            <Maximize2 className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onClose?.(); }}
            className="p-1 hover:bg-white/20 rounded transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={`fixed z-[100] select-none transition-all duration-200 ${isDragging ? 'cursor-grabbing ring-2 ring-purple-500/50' : 'cursor-grab hover:ring-1 hover:ring-white/20'}`}
      style={{
        left: position.x,
        top: position.y,
        width: size.width,
        height: size.height,
        backgroundColor: `rgba(0, 0, 0, ${opacity})`,
        backdropFilter: 'blur(8px)',
        borderRadius: '12px',
        boxShadow: isDragging ? '0 12px 48px rgba(106, 31, 191, 0.3)' : '0 8px 32px rgba(0, 0, 0, 0.4)',
        border: `1px solid rgba(255, 255, 255, ${isDragging ? '0.3' : '0.1'})`,
      }}
      onMouseDown={handleMouseDown}
    >
      {/* Countdown Overlay */}
      {countdown > 0 && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/80 rounded-xl z-10">
          <div className="text-center">
            <div className="text-6xl font-bold text-white mb-2">{countdown}</div>
            <div className="text-gray-400 text-sm">Starting in...</div>
          </div>
        </div>
      )}

      {/* Header / Drag Handle */}
      <div
        className="flex items-center justify-between px-3 py-2 cursor-grab border-b border-white/10"
      >
        <div className="flex items-center gap-2 text-white">
          <GripVertical className="w-4 h-4 text-gray-400 animate-pulse" />
          <span className="text-sm font-medium">Teleprompter</span>
          <span className="text-xs text-gray-500 hidden sm:inline">(Drag anywhere to move)</span>
          {isPlaying && <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />}
        </div>
        <div className="flex items-center gap-1 control-area">
          <button
            onClick={() => setShowControls(!showControls)}
            className="p-1.5 hover:bg-white/20 rounded transition-colors text-white"
            title={showControls ? "Hide Controls" : "Show Controls"}
          >
            <Eye className={`w-4 h-4 ${showControls ? '' : 'opacity-50'}`} />
          </button>
          <button
            onClick={handleMinimize}
            className="p-1.5 hover:bg-white/20 rounded transition-colors text-white"
            title="Minimize"
          >
            <Minimize2 className="w-4 h-4" />
          </button>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white/20 rounded transition-colors text-white"
            title="Close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Control Bar */}
      {showControls && (
        <div className="px-3 py-2 border-b border-white/10 control-area">
          <div className="flex items-center gap-3 flex-wrap">
            {/* Play/Pause */}
            <button
              onClick={handlePlayPause}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 hover:bg-purple-700 rounded-lg text-white text-sm transition-colors"
            >
              {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              {isPlaying ? 'Pause' : 'Play'}
            </button>

            {/* Reset */}
            <button
              onClick={handleReset}
              className="p-1.5 hover:bg-white/20 rounded transition-colors text-white"
              title="Reset to start"
            >
              <RotateCcw className="w-4 h-4" />
            </button>

            {/* Mirror Toggle */}
            <button
              onClick={() => setIsMirrored(!isMirrored)}
              className={`p-1.5 rounded transition-colors ${isMirrored ? 'bg-purple-600 text-white' : 'hover:bg-white/20 text-white'}`}
              title="Mirror text"
            >
              <FlipHorizontal className="w-4 h-4" />
            </button>

            {/* Focus Mode Toggle */}
            <button
              onClick={() => setIsFocusMode(!isFocusMode)}
              className={`p-1.5 rounded transition-colors ${isFocusMode ? 'bg-purple-600 text-white' : 'hover:bg-white/20 text-white'}`}
              title="Focus mode"
            >
              <Focus className="w-4 h-4" />
            </button>

            {/* AI Auto-Detect Toggle */}
            <button
              onClick={() => setAiAutoDetect(!aiAutoDetect)}
              className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-sm transition-colors ${
                aiAutoDetect
                  ? 'bg-gradient-to-r from-purple-600 to-teal-500 text-white'
                  : 'hover:bg-white/20 text-white'
              }`}
              title="AI Auto-detect speed - follows your speech"
            >
              {aiProcessing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Zap className="w-4 h-4" />
              )}
              <span className="text-xs hidden sm:inline">AI</span>
              {aiListening && (
                <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
              )}
            </button>

            {/* AI hearing indicator */}
            {aiAutoDetect && aiListening && lastHeard && (
              <span className="text-xs text-green-400/70 truncate max-w-[120px]" title={lastHeard}>
                🎤 {lastHeard}
              </span>
            )}

            {/* Opacity Control */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-400">BG</span>
              <input
                type="range"
                min="0.15"
                max="1"
                step="0.05"
                value={opacity}
                onChange={(e) => setOpacity(parseFloat(e.target.value))}
                className="w-16 h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-purple-500"
              />
              <span className="text-xs text-gray-400 w-7">{Math.round(opacity * 100)}%</span>
            </div>

            {/* Font Weight Control */}
            <div className="flex items-center gap-2">
              <Type className="w-4 h-4 text-gray-400" />
              <input
                type="range"
                min="400"
                max="900"
                step="100"
                value={fontWeight}
                onChange={(e) => setFontWeight(parseInt(e.target.value))}
                className="w-16 h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-purple-500"
              />
            </div>

            {/* Font Size Controls */}
            <div className="flex items-center gap-1">
              <button
                onClick={() => setCurrentFontSize(prev => Math.max(16, prev - 2))}
                className="p-1 hover:bg-white/20 rounded transition-colors text-white"
                title="Decrease font size"
              >
                <ChevronDown className="w-4 h-4" />
              </button>
              <span className="text-xs text-gray-400 w-7 text-center">{currentFontSize}</span>
              <button
                onClick={() => setCurrentFontSize(prev => Math.min(72, prev + 2))}
                className="p-1 hover:bg-white/20 rounded transition-colors text-white"
                title="Increase font size"
              >
                <ChevronUp className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Text Content */}
      <div
        ref={scrollRef}
        className="overflow-y-auto overflow-x-hidden cursor-text"
        style={{
          height: showControls ? 'calc(100% - 130px)' : 'calc(100% - 74px)',
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onScroll={(e) => setScrollPos(e.target.scrollTop)}
      >
        <div
          ref={contentRef}
          className="px-6 py-4"
          style={{
            paddingTop: '2rem',
            paddingBottom: '2rem',
            transform: isMirrored ? 'scaleX(-1)' : 'none',
          }}
        >
          <div
            className={`text-white ${isFocusMode ? 'focus-mode-text' : ''}`}
            style={{
              fontFamily: font,
              fontSize: `${currentFontSize}px`,
              fontWeight: fontWeight,
              textAlign: textAlign,
              lineHeight: currentLineHeight,
              letterSpacing: '0.02em',
            }}
          >
            {aiAutoDetect && scriptWordsRef.current.length > 0 ? (
              // Word-by-word rendering with highlighting for AI mode
              scriptWordsRef.current.map((word, idx) => {
                if (word.isSpace) {
                  // Render whitespace (preserve newlines)
                  return word.raw.includes('\n')
                    ? <br key={`sp-${idx}`} />
                    : <span key={`sp-${idx}`}> </span>;
                }
                const isMatched = word.index <= matchedWordIndex;
                const isCurrentWord = word.index === matchedWordIndex;
                return (
                  <span
                    key={`w-${idx}`}
                    data-word-idx={word.index}
                    style={{
                      transition: 'all 0.3s ease',
                      color: isMatched ? 'rgba(255,255,255,1)' : 'rgba(255,255,255,0.55)',
                      textDecoration: isCurrentWord ? 'underline' : 'none',
                      textDecorationColor: isCurrentWord ? 'rgba(139, 92, 246, 0.6)' : 'transparent',
                      textUnderlineOffset: '4px',
                      textDecorationThickness: '2px',
                    }}
                  >{word.raw}</span>
                );
              })
            ) : (
              <p className="whitespace-pre-wrap">
                {displayText || "Your teleprompter text will appear here...\n\nPress Play to start auto-scrolling."}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Position slider - allows manual correction of scroll position */}
      <div
        className="px-3 py-1.5 border-t border-white/10 control-area"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <input
          type="range"
          min="0"
          max={scrollRef.current ? Math.max(1, scrollRef.current.scrollHeight - scrollRef.current.clientHeight) : 1000}
          step="1"
          value={scrollPos}
          onChange={(e) => {
            const val = parseFloat(e.target.value);
            if (scrollRef.current) {
              scrollRef.current.scrollTop = val;
            }
            setScrollPos(val);
            // Also update AI scroll target so it doesn't fight the manual position
            if (aiAutoDetect) {
              aiScrollTargetRef.current = val;
              lastMatchTimeRef.current = Date.now(); // Prevent off-script timeout
            }
          }}
          className="w-full h-1 bg-gray-600 rounded-lg appearance-none cursor-pointer accent-purple-500"
          title="Drag to adjust position"
        />
      </div>

      {/* Keyboard hint */}
      <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 text-xs text-gray-500 whitespace-nowrap">
        <kbd className="px-1.5 py-0.5 bg-white/10 rounded">Space</kbd> Play/Pause
        <kbd className="px-1.5 py-0.5 bg-white/10 rounded ml-2">↑</kbd>
        <kbd className="px-1.5 py-0.5 bg-white/10 rounded">↓</kbd> Scroll
      </div>

      {/* Resize Handles */}
      <div
        className="resize-handle absolute top-0 left-0 w-3 h-3 cursor-nw-resize"
        onMouseDown={handleResizeStart('nw')}
      />
      <div
        className="resize-handle absolute top-0 right-0 w-3 h-3 cursor-ne-resize"
        onMouseDown={handleResizeStart('ne')}
      />
      <div
        className="resize-handle absolute bottom-0 left-0 w-3 h-3 cursor-sw-resize"
        onMouseDown={handleResizeStart('sw')}
      />
      <div
        className="resize-handle absolute bottom-0 right-0 w-3 h-3 cursor-se-resize"
        onMouseDown={handleResizeStart('se')}
      />
      <div
        className="resize-handle absolute top-0 left-3 right-3 h-1 cursor-n-resize"
        onMouseDown={handleResizeStart('n')}
      />
      <div
        className="resize-handle absolute bottom-0 left-3 right-3 h-1 cursor-s-resize"
        onMouseDown={handleResizeStart('s')}
      />
      <div
        className="resize-handle absolute left-0 top-3 bottom-3 w-1 cursor-w-resize"
        onMouseDown={handleResizeStart('w')}
      />
      <div
        className="resize-handle absolute right-0 top-3 bottom-3 w-1 cursor-e-resize"
        onMouseDown={handleResizeStart('e')}
      />

      {/* Focus mode gradient overlay */}
      {isFocusMode && (
        <>
          <div
            className="absolute left-0 right-0 pointer-events-none"
            style={{
              top: showControls ? '100px' : '44px',
              height: '30%',
              background: 'linear-gradient(to bottom, rgba(0,0,0,0.8), transparent)',
            }}
          />
          <div
            className="absolute left-0 right-0 bottom-8 pointer-events-none"
            style={{
              height: '30%',
              background: 'linear-gradient(to top, rgba(0,0,0,0.8), transparent)',
            }}
          />
        </>
      )}
    </div>
  );
};

export default FloatingTeleprompter;
